import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTourForRoute, PAGE_TOURS } from './tourSteps';
import type { TourStep } from './tourSteps';
import './GuidedTour.css';

const STORAGE_KEY = 'stationExplorer_tourCompleted';

function isTourCompleted(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

function markTourCompleted() {
  localStorage.setItem(STORAGE_KEY, 'true');
}


export const GuidedTour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Show tour if first-time user — react to route changes (handles redirect from / to /search/station)
  useEffect(() => {
    if (isTourCompleted() || isActive) return;
    const tour = getTourForRoute(location.pathname);
    if (tour) {
      // Delay slightly to let page elements render
      const timer = setTimeout(() => {
        setSteps(tour.steps);
        setIsActive(true);
        setCurrentStep(0);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isActive]);


  // Position tooltip near target element
  const positionTooltip = useCallback(() => {
    if (!steps[currentStep]) return;
    const el = document.querySelector(steps[currentStep].target);
    if (!el) {
      // Target not found — show tooltip centered on screen
      setHighlightRect(null);
      setTooltipPos({ top: window.innerHeight / 3, left: Math.max(16, (window.innerWidth - 320) / 2) });
      return;
    }
    const rect = el.getBoundingClientRect();
    setHighlightRect(rect);
    const placement = steps[currentStep].placement;
    const tooltipWidth = 320;
    const margin = 16;
    let top = 0, left = 0;
    switch (placement) {
      case 'bottom': top = rect.bottom + 12; left = rect.left + rect.width / 2 - tooltipWidth / 2; break;
      case 'top': top = rect.top - 12 - 160; left = rect.left + rect.width / 2 - tooltipWidth / 2; break;
      case 'left': top = rect.top + rect.height / 2 - 80; left = rect.left - tooltipWidth - 12; break;
      case 'right': top = rect.top + rect.height / 2 - 80; left = rect.right + 12; break;
    }
    // Clamp within viewport
    left = Math.max(margin, Math.min(left, window.innerWidth - tooltipWidth - margin));
    top = Math.max(margin, top);
    setTooltipPos({ top, left });
  }, [currentStep, steps]);

  useEffect(() => {
    if (!isActive) return;
    // Delay to let DOM settle after route change
    const timer = setTimeout(positionTooltip, 300);
    window.addEventListener('resize', positionTooltip);
    return () => { clearTimeout(timer); window.removeEventListener('resize', positionTooltip); };
  }, [isActive, positionTooltip]);


  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // End of this page's tour — navigate to next page tour or finish
      const allRoutes = PAGE_TOURS.map(t => t.route);
      const currentIdx = allRoutes.indexOf(
        PAGE_TOURS.find(t => location.pathname.startsWith(t.route))?.route || ''
      );
      if (currentIdx >= 0 && currentIdx < allRoutes.length - 1) {
        const nextRoute = allRoutes[currentIdx + 1];
        navigate(nextRoute);
        const nextTour = getTourForRoute(nextRoute);
        if (nextTour) {
          setSteps(nextTour.steps);
          setCurrentStep(0);
        } else {
          finishTour();
        }
      } else {
        finishTour();
      }
    }
  };

  const handleSkip = () => finishTour();

  const finishTour = () => {
    setIsActive(false);
    markTourCompleted();
    setHighlightRect(null);
  };


  if (!isActive || !steps[currentStep]) return null;

  const step = steps[currentStep];
  const totalPages = PAGE_TOURS.length;
  const currentPageIdx = PAGE_TOURS.findIndex(t => location.pathname.startsWith(t.route));
  const pageTour = PAGE_TOURS[currentPageIdx];

  return (
    <>
      {/* Overlay backdrop */}
      <div className="tour-overlay" onClick={handleSkip} />

      {/* Highlight cutout */}
      {highlightRect && (
        <div
          className="tour-highlight"
          style={{
            top: highlightRect.top - 4,
            left: highlightRect.left - 4,
            width: highlightRect.width + 8,
            height: highlightRect.height + 8,
          }}
        />
      )}


      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`tour-tooltip tour-tooltip--${step.placement}`}
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        <div className="tour-tooltip__header">
          <span className="tour-tooltip__page">
            {pageTour?.pageName} ({currentPageIdx + 1}/{totalPages})
          </span>
          <button
            className="tour-tooltip__close"
            onClick={handleSkip}
            aria-label="Skip tour"
          >
            ✕
          </button>
        </div>
        <h4 className="tour-tooltip__title">{step.title}</h4>
        <p className="tour-tooltip__content">{step.content}</p>
        <div className="tour-tooltip__footer">
          <span className="tour-tooltip__progress">
            Step {currentStep + 1} of {steps.length}
          </span>
          <div className="tour-tooltip__actions">
            <button className="tour-tooltip__skip" onClick={handleSkip}>
              Skip Tour
            </button>
            <button className="tour-tooltip__next" onClick={handleNext}>
              {currentStep === steps.length - 1 && currentPageIdx === totalPages - 1
                ? 'Finish'
                : currentStep === steps.length - 1
                ? 'Next Page →'
                : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
